import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert, Avatar, Box, Button, ButtonGroup, Card, CardContent, CircularProgress,
  Grid, IconButton, LinearProgress, Stack, Tooltip, Typography
} from '@mui/material';
import {
  ArrowDownward, ArrowUpward, CancelOutlined, CheckCircleOutline, Groups,
  LocalShipping, Refresh, ShowChart, ShoppingCart, Storefront, CurrencyRupee
} from '@mui/icons-material';

const PERIODS = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 365, label: '12 months' },
];

const STATUS_COLORS = {
  completed: '#0b9b59',
  cancelled: '#ef6461',
  in_progress: '#7666db',
  driver_arrived: '#ed9e31',
  confirmed: '#3285d5',
  searching_driver: '#e7b43f',
  scheduled: '#27a8a0',
  pending: '#96a2b3',
};

const formatNumber = (value) => new Intl.NumberFormat('en-IN').format(Number(value) || 0);
const formatCurrency = (value) => `₹${formatNumber(Math.round(Number(value) || 0))}`;
const weekBucket = (date) => {
  const thursday = new Date(date);
  thursday.setUTCDate(thursday.getUTCDate() + 4 - (thursday.getUTCDay() || 7));
  const year = thursday.getUTCFullYear();
  const dayOfYear = Math.floor((thursday - new Date(Date.UTC(year, 0, 1))) / 86400000) + 1;
  return `${year}-W${String(Math.ceil(dayOfYear / 7)).padStart(2, '0')}`;
};
const formatBucket = (bucket, monthly) => {
  if (bucket.includes('-W')) {
    const [year, week] = bucket.split('-W').map(Number);
    const jan4 = new Date(Date.UTC(year, 0, 4));
    jan4.setUTCDate(jan4.getUTCDate() - ((jan4.getUTCDay() + 6) % 7) + (week - 1) * 7);
    return jan4.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  }
  const date = new Date(`${bucket}${monthly ? '-01' : 'T00:00:00Z'}`);
  return Number.isNaN(date.getTime()) ? bucket : date.toLocaleDateString('en-IN', monthly
    ? { month: 'short', year: '2-digit', timeZone: 'UTC' }
    : { day: 'numeric', month: 'short', timeZone: 'UTC' });
};

const percentChange = (current, previous) => {
  const now = Number(current) || 0;
  const before = Number(previous) || 0;
  if (before === 0) return now === 0 ? '0%' : 'New';
  const change = ((now - before) / before) * 100;
  return `${change > 0 ? '+' : ''}${Math.round(change)}%`;
};

function MetricCard({ label, value, icon, accent, comparison, detail, inverse = false }) {
  const rawUp = comparison && comparison !== 'New' && !comparison.startsWith('-') && comparison !== '0%';
  const isUp = inverse ? comparison && comparison.startsWith('-') : rawUp;
  const isNew = comparison === 'New';
  return (
    <Card sx={{ height: '100%', border: '1px solid #e9eef2', boxShadow: '0 5px 20px rgba(22,48,36,.045)', borderRadius: 3 }}>
      <CardContent sx={{ p: { xs: 1.7, sm: 2.2 }, '&:last-child': { pb: { xs: 1.7, sm: 2.2 } } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.7 }}>
          <Avatar sx={{ width: 42, height: 42, bgcolor: `${accent}16`, color: accent, borderRadius: 2 }}>{icon}</Avatar>
          {comparison && (
            <Stack direction="row" spacing={0.35} alignItems="center" sx={{ color: isNew ? '#65758b' : isUp ? '#07864f' : comparison === '0%' ? '#65758b' : '#df5753' }}>
              {isUp ? <ArrowUpward sx={{ fontSize: 15 }} /> : !isNew && comparison !== '0%' ? <ArrowDownward sx={{ fontSize: 15 }} /> : null}
              <Typography variant="caption" fontWeight={800}>{comparison}</Typography>
            </Stack>
          )}
        </Stack>
        <Typography sx={{ color: '#182a25', fontSize: { xs: '1.35rem', sm: '1.65rem' }, lineHeight: 1.15, fontWeight: 850, letterSpacing: '-.04em', overflowWrap: 'anywhere' }}>
          {value}
        </Typography>
        <Typography sx={{ color: '#687a73', fontSize: '.83rem', fontWeight: 650, mt: .65 }}>{label}</Typography>
        {detail && <Typography sx={{ color: '#9aa7a1', fontSize: '.7rem', mt: .35 }}>{detail}</Typography>}
      </CardContent>
    </Card>
  );
}

function PerformanceChart({ series, mode, monthly }) {
  const [activeIndex, setActiveIndex] = useState(null);
  const values = series.map((item) => Math.max(0, Number(item[mode]) || 0));
  const max = Math.max(...values, 1);
  const width = 760;
  const height = 250;
  const left = 12;
  const right = 12;
  const top = 18;
  const bottom = 35;
  const plotHeight = height - top - bottom;
  const step = series.length > 1 ? (width - left - right) / (series.length - 1) : 0;
  const xFor = (index) => series.length === 1 ? width / 2 : left + index * step;
  const yFor = (value) => top + plotHeight - ((value / max) * plotHeight);
  const linePath = series.map((item, index) => `${index === 0 ? 'M' : 'L'} ${xFor(index)} ${yFor(Number(item[mode]) || 0)}`).join(' ');
  const areaPath = `${linePath} L ${xFor(series.length - 1)} ${top + plotHeight} L ${xFor(0)} ${top + plotHeight} Z`;
  const labelIndexes = series.length <= 7
    ? series.map((_, index) => index)
    : [...new Set([0, Math.round((series.length - 1) / 3), Math.round(((series.length - 1) * 2) / 3), series.length - 1])];

  useEffect(() => setActiveIndex(null), [series, mode]);

  if (!series.length) return <Box sx={{ height: 270, display: 'grid', placeItems: 'center', color: '#82918a' }}>No performance data for this period yet.</Box>;

  return (
    <Box sx={{ position: 'relative' }}>
      <Box sx={{ height: 38, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1 }}>
        <Typography sx={{ fontSize: '.8rem', fontWeight: 800, color: '#263a32' }}>{activeIndex === null ? 'Period performance' : formatBucket(series[activeIndex].bucket, monthly)}</Typography>
        <Typography sx={{ color: '#0b8150', fontSize: '.95rem', fontWeight: 850 }}>
          {activeIndex === null ? (mode === 'orders' ? 'Orders' : 'Completed booking value') : (mode === 'orders' ? formatNumber(series[activeIndex].orders) : formatCurrency(series[activeIndex].completedValue))}
        </Typography>
      </Box>
      <Box sx={{ width: '100%', overflow: 'hidden' }}>
        <svg viewBox={`0 0 ${width} ${height}`} width="100%" height="250" role="img" aria-label={`${mode === 'orders' ? 'Order' : 'Completed booking value'} by ${monthly ? 'month' : 'day'}`} preserveAspectRatio="none">
          {[0, .25, .5, .75, 1].map((fraction) => {
            const y = top + plotHeight * fraction;
            return <g key={fraction}><line x1={left} x2={width - right} y1={y} y2={y} stroke="#edf1ef" strokeDasharray="4 5" /><text x={left} y={y - 4} fill="#91a099" fontSize="10">{formatNumber(Math.round(max * (1 - fraction)))}</text></g>;
          })}
          {mode === 'revenue' && <><defs><linearGradient id="analyticsArea" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor="#0b9b59" stopOpacity=".2" /><stop offset="100%" stopColor="#0b9b59" stopOpacity="0" /></linearGradient></defs><path d={areaPath} fill="url(#analyticsArea)" /></>}
          {mode === 'revenue' && <path d={linePath} fill="none" stroke="#0b9b59" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />}
          {series.map((item, index) => {
            const value = Number(item[mode]) || 0;
            const x = xFor(index);
            const y = yFor(value);
            const selected = activeIndex === index;
            if (mode === 'orders') {
              const barWidth = Math.min(34, (width - left - right) / Math.max(series.length, 1) * .56);
              const barHeight = Math.max(2, top + plotHeight - y);
              return <g key={item.bucket} onMouseEnter={() => setActiveIndex(index)} onMouseLeave={() => setActiveIndex(null)} onFocus={() => setActiveIndex(index)} onBlur={() => setActiveIndex(null)} tabIndex="0" role="img" aria-label={`${formatBucket(item.bucket, monthly)}: ${formatNumber(value)} orders`}>
                <title>{`${formatBucket(item.bucket, monthly)} · ${formatNumber(value)} orders`}</title>
                <rect x={x - barWidth / 2} y={y} width={barWidth} height={barHeight} rx="6" fill={selected ? '#087b48' : '#20ad6b'} opacity={selected ? 1 : .82} />
                <rect x={x - Math.max(barWidth / 2, 10)} y={top} width={Math.max(barWidth, 20)} height={plotHeight} fill="transparent" />
                {selected && <circle cx={x} cy={y} r="4" fill="#fff" stroke="#087b48" strokeWidth="2" />}
              </g>;
            }
            return <g key={item.bucket} onMouseEnter={() => setActiveIndex(index)} onMouseLeave={() => setActiveIndex(null)} onFocus={() => setActiveIndex(index)} onBlur={() => setActiveIndex(null)} tabIndex="0" role="img" aria-label={`${formatBucket(item.bucket, monthly)}: ${formatCurrency(value)} completed booking value`}>
              <title>{`${formatBucket(item.bucket, monthly)} · ${formatCurrency(value)}`}</title>
              <circle cx={x} cy={y} r={selected ? 6 : 4} fill="#fff" stroke="#07864f" strokeWidth="3" />
              <circle cx={x} cy={y} r="12" fill="transparent" />
            </g>;
          })}
          {labelIndexes.map((index) => <text key={series[index].bucket} x={xFor(index)} y={height - 9} fill="#83918b" fontSize="10" textAnchor={index === 0 ? 'start' : index === series.length - 1 ? 'end' : 'middle'}>{formatBucket(series[index].bucket, monthly)}</text>)}
        </svg>
      </Box>
    </Box>
  );
}

function StatusBreakdown({ statuses, total }) {
  const entries = Object.entries(statuses || {}).sort((a, b) => b[1] - a[1]);
  const circumference = 2 * Math.PI * 42;
  let offset = 0;
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="center" sx={{ minHeight: 248, p: { xs: 1, sm: 2 } }}>
      <Box sx={{ width: 150, height: 150, flex: '0 0 auto', position: 'relative' }}>
        <svg viewBox="0 0 100 100" width="150" height="150" role="img" aria-label={`${formatNumber(total)} total orders by status`}>
          <circle cx="50" cy="50" r="42" fill="none" stroke="#edf1ef" strokeWidth="12" />
          {entries.map(([status, count]) => {
            const length = total > 0 ? (count / total) * circumference : 0;
            const circle = <circle key={status} cx="50" cy="50" r="42" fill="none" stroke={STATUS_COLORS[status] || '#84938c'} strokeWidth="12" strokeDasharray={`${length} ${circumference - length}`} strokeDashoffset={-offset} transform="rotate(-90 50 50)"><title>{`${status.replace(/_/g, ' ')}: ${formatNumber(count)}`}</title></circle>;
            offset += length;
            return circle;
          })}
        </svg>
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 850, color: '#20342b' }}>{formatNumber(total)}</Typography>
          <Typography sx={{ fontSize: '.68rem', color: '#85938d', fontWeight: 650 }}>orders</Typography>
        </Box>
      </Box>
      <Stack spacing={1} sx={{ width: '100%' }}>
        {entries.length ? entries.map(([status, count]) => (
          <Stack key={status} direction="row" alignItems="center" spacing={1}>
            <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: STATUS_COLORS[status] || '#84938c', flex: '0 0 auto' }} />
            <Typography sx={{ textTransform: 'capitalize', color: '#596a62', fontSize: '.76rem', flex: 1 }}>{status.replace(/_/g, ' ')}</Typography>
            <Typography sx={{ color: '#283a32', fontSize: '.76rem', fontWeight: 800 }}>{formatNumber(count)}</Typography>
          </Stack>
        )) : <Typography color="text.secondary" variant="body2">No orders in this range.</Typography>}
      </Stack>
    </Stack>
  );
}

function MembershipTrend({ series }) {
  const [activeIndex, setActiveIndex] = useState(null);
  const categories = [
    { key: 'active', label: 'Activated', color: '#0b9b59' },
    { key: 'pending', label: 'Awaiting review', color: '#e6a52f' },
    { key: 'info_requested', label: 'Info requested', color: '#4789d5' },
    { key: 'rejected', label: 'Rejected', color: '#e36666' },
  ];
  const max = Math.max(...series.map((point) => categories.reduce((sum, category) => sum + (Number(point[category.key]) || 0), 0)), 1);
  const width = 760;
  const height = 250;
  const left = 12;
  const right = 12;
  const top = 20;
  const bottom = 38;
  const plotHeight = height - top - bottom;
  const step = series.length > 1 ? (width - left - right) / series.length : width - left - right;
  const barWidth = Math.min(38, step * .55);
  const selected = activeIndex === null ? null : series[activeIndex];

  if (!series.length) return <Box sx={{ height: 230, display: 'grid', placeItems: 'center', color: '#82918a', textAlign: 'center' }}>Membership purchase history starts with new subscription requests after this feature is deployed.</Box>;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1, minHeight: 38 }}>
        <Typography sx={{ fontSize: '.8rem', fontWeight: 800, color: '#263a32' }}>{selected ? formatBucket(selected.bucket, false) : 'Subscriptions by period'}</Typography>
        <Typography sx={{ color: '#20352b', fontSize: '.78rem', fontWeight: 800 }}>
          {selected ? `${categories.reduce((sum, category) => sum + (Number(selected[category.key]) || 0), 0)} request(s)` : `${formatNumber(series.reduce((sum, point) => sum + categories.reduce((rowSum, category) => rowSum + (Number(point[category.key]) || 0), 0), 0))} total`}
        </Typography>
      </Stack>
      <Box sx={{ width: '100%', overflow: 'hidden' }}>
        <svg viewBox={`0 0 ${width} ${height}`} width="100%" height="240" role="img" aria-label="Driver membership requests and activations over time" preserveAspectRatio="none">
          {[0, .5, 1].map((fraction) => {
            const y = top + plotHeight * fraction;
            return <g key={fraction}><line x1={left} x2={width - right} y1={y} y2={y} stroke="#edf1ef" strokeDasharray="4 5" /><text x={left} y={y - 4} fill="#91a099" fontSize="10">{formatNumber(Math.round(max * (1 - fraction)))}</text></g>;
          })}
          {series.map((point, index) => {
            const x = left + step * (index + .5);
            let accumulated = 0;
            const total = categories.reduce((sum, category) => sum + (Number(point[category.key]) || 0), 0);
            return <g key={point.bucket} onMouseEnter={() => setActiveIndex(index)} onMouseLeave={() => setActiveIndex(null)} onFocus={() => setActiveIndex(index)} onBlur={() => setActiveIndex(null)} tabIndex="0" role="img" aria-label={`${formatBucket(point.bucket, false)}: ${total} membership requests`}>
              <title>{`${formatBucket(point.bucket, false)} · ${categories.map((category) => `${category.label}: ${formatNumber(point[category.key])}`).join(' · ')}`}</title>
              {categories.map((category) => {
                const count = Number(point[category.key]) || 0;
                const segmentHeight = (count / max) * plotHeight;
                const y = top + plotHeight - accumulated - segmentHeight;
                accumulated += segmentHeight;
                return count ? <rect key={category.key} x={x - barWidth / 2} y={y} width={barWidth} height={Math.max(segmentHeight, 2)} rx="3" fill={category.color} /> : null;
              })}
              <rect x={x - Math.max(barWidth / 2, 12)} y={top} width={Math.max(barWidth, 24)} height={plotHeight} fill="transparent" />
              <text x={x} y={height - 10} fill="#83918b" fontSize="10" textAnchor="middle">{formatBucket(point.bucket, false)}</text>
            </g>;
          })}
        </svg>
      </Box>
      <Stack direction="row" spacing={{ xs: 1, sm: 2.5 }} useFlexGap flexWrap="wrap" sx={{ px: 1, py: 1.2 }}>
        {categories.map((category) => <Stack key={category.key} direction="row" spacing={.7} alignItems="center"><Box sx={{ width: 9, height: 9, borderRadius: .5, bgcolor: category.color }} /><Typography sx={{ color: '#687a73', fontSize: '.72rem' }}>{category.label}</Typography></Stack>)}
      </Stack>
    </Box>
  );
}

export default function BusinessAnalytics() {
  const [range, setRange] = useState(30);
  const [mode, setMode] = useState('orders');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [requestKey, setRequestKey] = useState(0);

  const refresh = useCallback(() => setRequestKey((key) => key + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    setRefreshing(true);
    setError('');
    axios.get('/admin/analytics', { params: { range }, signal: controller.signal })
      .then((response) => setData(response.data?.data || response.data))
      .catch((err) => {
        if (err.code !== 'ERR_CANCELED') setError(err.response?.data?.message || 'Could not load business analytics. Please try again.');
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
          setRefreshing(false);
        }
      });
    return () => controller.abort();
  }, [range, requestKey]); // Refresh only when the selected range or refresh action changes.

  const series = useMemo(() => {
    if (!data?.series) return [];
    const rangeStart = new Date(data.start);
    const rangeEnd = new Date(data.end);
    const monthly = range === 365;
    const weekly = range === 30;
    const points = new Map(data.series.map((point) => [point.bucket, point]));
    const buckets = [];
    if (monthly) {
      const firstMonth = new Date(Date.UTC(rangeStart.getUTCFullYear(), rangeStart.getUTCMonth(), 1));
      for (let cursor = firstMonth; cursor < rangeEnd; cursor.setUTCMonth(cursor.getUTCMonth() + 1)) {
        buckets.push(cursor.toISOString().slice(0, 7));
      }
    } else if (weekly) {
      const cursor = new Date(rangeStart);
      cursor.setUTCDate(cursor.getUTCDate() - ((cursor.getUTCDay() + 6) % 7));
      while (cursor < rangeEnd) {
        buckets.push(weekBucket(cursor));
        cursor.setUTCDate(cursor.getUTCDate() + 7);
      }
    } else {
      const cursor = new Date(rangeStart);
      while (cursor < rangeEnd) {
        buckets.push(cursor.toISOString().slice(0, 10));
        cursor.setUTCDate(cursor.getUTCDate() + 1);
      }
    }
    return buckets.map((bucket) => ({ bucket, orders: 0, completedValue: 0, ...(points.get(bucket) || {}) }));
  }, [data, range]);
  const membershipSeries = useMemo(() => {
    const points = new Map((data?.memberships?.series || []).map((point) => [point.bucket, point]));
    return series.map((point) => ({
      bucket: point.bucket,
      active: 0,
      pending: 0,
      info_requested: 0,
      rejected: 0,
      ...(points.get(point.bucket) || {})
    }));
  }, [data, series]);

  const totals = data?.totals || {};
  const previous = data?.previous || {};
  const totalOrders = Number(totals.orders) || 0;
  const completionRate = totalOrders ? Math.round(((Number(totals.completed) || 0) / totalOrders) * 100) : 0;
  const monthly = range === 365;

  if (loading && !data) return <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 360 }}><CircularProgress color="success" /></Box>;

  return (
    <Box sx={{ maxWidth: 1500, mx: 'auto', pb: 4 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ color: '#16372a', fontSize: { xs: '1.65rem', sm: '2rem' }, fontWeight: 850, letterSpacing: '-.04em' }}>Business Analytics</Typography>
          <Typography sx={{ color: '#718078', mt: .5, fontSize: '.9rem' }}>A clear view of orders, completed booking value, and business growth.</Typography>
        </Box>
        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
          <ButtonGroup size="small" aria-label="Analytics date range" sx={{ bgcolor: '#fff', '& .MuiButton-root': { textTransform: 'none', fontWeight: 700, px: { xs: 1.1, sm: 1.6 } } }}>
            {PERIODS.map((period) => <Button key={period.value} variant={range === period.value ? 'contained' : 'outlined'} onClick={() => setRange(period.value)}>{period.label}</Button>)}
          </ButtonGroup>
          <Tooltip title="Refresh analytics"><span><IconButton onClick={refresh} disabled={refreshing} aria-label="Refresh analytics" sx={{ bgcolor: '#fff', border: '1px solid #e4ece7' }}><Refresh /></IconButton></span></Tooltip>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={refresh}>Retry</Button>}>{error}</Alert>}
      {refreshing && <LinearProgress color="success" sx={{ mb: 1, borderRadius: 2 }} />}

      <Grid container spacing={1.8} sx={{ mb: 2.5 }}>
        <Grid item xs={6} sm={4} lg={2}><MetricCard label="Customers" value={formatNumber(totals.customers)} icon={<Groups />} accent="#3985d6" detail="Registered accounts" /></Grid>
        <Grid item xs={6} sm={4} lg={2}><MetricCard label="Drivers" value={formatNumber(totals.drivers)} icon={<LocalShipping />} accent="#8d68d5" detail="Driver accounts" /></Grid>
        <Grid item xs={6} sm={4} lg={2}><MetricCard label="Owners" value={formatNumber(totals.owners)} icon={<Storefront />} accent="#dc8d35" detail="Owner accounts" /></Grid>
        <Grid item xs={6} sm={4} lg={2}><MetricCard label="Orders" value={formatNumber(totalOrders)} icon={<ShoppingCart />} accent="#07864f" comparison={percentChange(totalOrders, previous.orders)} detail={`vs previous ${range === 365 ? '12 months' : `${range} days`}`} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><MetricCard label="Completed" value={formatNumber(totals.completed)} icon={<CheckCircleOutline />} accent="#0b9b59" comparison={percentChange(totals.completed, previous.completed)} detail={`${completionRate}% completion rate`} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><MetricCard label="Cancelled" value={formatNumber(totals.cancelled)} icon={<CancelOutlined />} accent="#df5753" comparison={percentChange(totals.cancelled, previous.cancelled)} detail="In selected period" inverse /></Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={8}>
          <Card sx={{ height: '100%', border: '1px solid #e9eef2', boxShadow: '0 5px 20px rgba(22,48,36,.045)', borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
              <Box><Typography sx={{ fontWeight: 800, color: '#20352b', fontSize: '1rem' }}>Performance over time</Typography><Typography sx={{ color: '#89968f', fontSize: '.75rem', mt: .3 }}>{monthly ? 'Monthly' : range === 30 ? 'Weekly' : 'Daily'} activity · UTC</Typography></Box>
                <ButtonGroup size="small" aria-label="Performance metric">
                  <Button startIcon={<ShoppingCart />} variant={mode === 'orders' ? 'contained' : 'outlined'} onClick={() => setMode('orders')} sx={{ textTransform: 'none' }}>Orders</Button>
                  <Button startIcon={<CurrencyRupee />} variant={mode === 'revenue' ? 'contained' : 'outlined'} onClick={() => setMode('revenue')} sx={{ textTransform: 'none' }}>Value</Button>
                </ButtonGroup>
              </Stack>
              <PerformanceChart series={series} mode={mode} monthly={monthly} />
              {mode === 'revenue' && <Typography sx={{ color: '#89968f', fontSize: '.7rem', mt: .5 }}>Value sums booking amounts for completed orders; it is not a payment settlement report.</Typography>}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: '100%', border: '1px solid #e9eef2', boxShadow: '0 5px 20px rgba(22,48,36,.045)', borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
              <Typography sx={{ fontWeight: 800, color: '#20352b', fontSize: '1rem' }}>Order status mix</Typography>
              <Typography sx={{ color: '#89968f', fontSize: '.75rem', mt: .3 }}>Selected period · {formatNumber(totalOrders)} orders</Typography>
              <StatusBreakdown statuses={data?.statuses} total={totalOrders} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card sx={{ border: '1px solid #e9eef2', boxShadow: '0 5px 20px rgba(22,48,36,.045)', borderRadius: 3 }}>
            <CardContent sx={{ p: 2.2 }}>
              <Stack direction="row" alignItems="center" spacing={1.2}>
                <Avatar sx={{ bgcolor: '#e7f7ef', color: '#07864f' }}><ShowChart /></Avatar>
                <Box><Typography sx={{ color: '#7b8982', fontSize: '.78rem', fontWeight: 650 }}>Completed booking value</Typography><Typography sx={{ fontSize: '1.5rem', fontWeight: 850, color: '#17372a' }}>{formatCurrency(totals.completedValue)}</Typography></Box>
                <Box sx={{ ml: 'auto !important', color: percentChange(totals.completedValue, previous.completedValue).startsWith('-') ? '#df5753' : '#07864f', fontWeight: 800 }}>{percentChange(totals.completedValue, previous.completedValue)}</Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card sx={{ border: '1px solid #e9eef2', boxShadow: '0 5px 20px rgba(22,48,36,.045)', borderRadius: 3 }}>
            <CardContent sx={{ p: 2.2 }}>
              <Stack direction="row" alignItems="center" spacing={1.2}>
                <Avatar sx={{ bgcolor: '#fff3e4', color: '#d8892e' }}><ShoppingCart /></Avatar>
                <Box><Typography sx={{ color: '#7b8982', fontSize: '.78rem', fontWeight: 650 }}>Order growth</Typography><Typography sx={{ fontSize: '1.5rem', fontWeight: 850, color: '#17372a' }}>{percentChange(totalOrders, previous.orders)}</Typography></Box>
                <Typography sx={{ color: '#89968f', fontSize: '.72rem', ml: 'auto !important', textAlign: 'right' }}>Compared with the<br />previous equal period</Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card sx={{ border: '1px solid #e9eef2', boxShadow: '0 5px 20px rgba(22,48,36,.045)', borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                <Box><Typography sx={{ fontWeight: 800, color: '#20352b', fontSize: '1rem' }}>Driver membership purchases</Typography><Typography sx={{ color: '#89968f', fontSize: '.75rem', mt: .3 }}>Subscription requests by period and review status · {monthly ? 'monthly' : range === 30 ? 'weekly' : 'daily'}</Typography></Box>
                <Typography sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, px: 1.3, py: .7, borderRadius: 2, bgcolor: '#edf8f1', color: '#087b48', fontSize: '.78rem', fontWeight: 800 }}>{formatNumber(data?.memberships?.currentActive || 0)} active now</Typography>
              </Stack>
              <MembershipTrend series={membershipSeries} />
              <Typography sx={{ color: '#89968f', fontSize: '.7rem', px: 1 }}>New requests and verified payments are recorded from this release onward; older memberships have no reliable purchase timestamp. Manual submissions are counted as pending until admin approval.</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
